import type { GraphModification, DescriptorDatatype, Graph } from "@lr-notitia/notitia-designer/schemas";
import { Ref, computed } from "vue";
import { api } from "@lr-notitia/notitia-designer/client";
import { handleGraphModification } from "./utils";
import { useSymbolsStore } from "../symbols";

let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	symbolsStore = useSymbolsStore();
}

export const datatypes = (graph: Ref<Graph>) =>
	computed<DescriptorDatatype[]>(() => {
		if (!symbolsStore) init();
		return (
			Object.values(graph.value.nodes).filter((node) => node.type === "datatype") as DescriptorDatatype[]
		).sort((a, b) =>
			symbolsStore
				.term({ id: a.refersTo, terminology: a.terminology || {} })
				.toLocaleLowerCase()
				.localeCompare(symbolsStore.term({ id: b.refersTo, terminology: b.terminology || {} }).toLocaleLowerCase())
		);
	});
export function datatype(domainId: Ref<string>, graph: Ref<Graph>) {
	return {
		byId: (id: string) => byId(id, graph),
		update: (datatype: DescriptorDatatype) => update(domainId, graph, datatype),
		remove: (id: string) => remove(domainId, graph, id),
		isImmutable: (id: string) => isImmutable(id),
	};
}

function byId(id: string, graph: Ref<Graph>): DescriptorDatatype | undefined {
	let hit = graph.value.nodes[id];
	return hit && hit.type === "datatype" && hit.layer === "descriptor" ? (hit as DescriptorDatatype) : undefined;
}

function update(domainId: Ref<string>, graph: Ref<Graph>, datatype: DescriptorDatatype): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[datatype.id]) return reject();
		api()
			.domain(domainId.value)
			.descriptor()
			.datatype(datatype.id)
			.put(datatype)
			.then((data) => {
				handleGraphModification(data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function remove(domainId: Ref<string>, graph: Ref<Graph>, id: string): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[id]) return reject();
		api()
			.domain(domainId.value)
			.descriptor()
			.datatype(id)
			.del()
			.then((data) => {
				handleGraphModification(data);
				resolve(data);
			})
			.catch(() => reject());
	});
}

function isImmutable(id: string): boolean {
	return immutableDatatypeIds.includes(id);
}

const immutableDatatypeIds = ["boolean", "string", "number", "date", "time", "timestamp", "array"];
